import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { BannerSearchParams } from '../models/banner-search.model'
import { Banner } from '../models/banner.entity'
import { BannersService } from '../banners.service'
import { Page } from '../../../models/util.model'
import { BannersRepository } from '../banners.repository'

interface BannersListingState {
  filters?: BannerSearchParams
  data?: Banner[]
  totalCount?: number
  error?: HttpErrorResponse
  isLoading: boolean
  isInitialized: boolean
}

const BANNERS_LISTING_INITIAL_STATE: BannersListingState = {
  isLoading: false,
  isInitialized: false,
}

@Injectable()
export class BannersListingStore {
  constructor(
    private bannersService: BannersService,
    private bannersRepository: BannersRepository,
  ) {}

  private state = signal<BannersListingState>(BANNERS_LISTING_INITIAL_STATE)

  // Selectors

  readonly isInitialized = computed(() => this.state().isInitialized)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly filters = computed(() => this.state().filters)
  readonly isFiltered = computed(() => {
    const filters = this.state().filters
    return !!filters && Object.keys(filters).length > 0
  })
  readonly isErrored = computed(() => !!this.state().error)
  readonly banners = computed(() => this.state().data || [])
  readonly totalCount = computed(() => this.state().totalCount || 0)

  // Effects

  async search(
    params?: BannerSearchParams,
    returnAll?: boolean,
  ): Promise<void> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.bannersRepository.searchBanners$(params ?? {}, returnAll).subscribe({
        next: (data) => {
          resolve()
          this.setData(data)
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async delete(banner: Banner): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.bannersRepository
        .alert('BANNER_DELETION', banner)
        .then((response) => {
          if (response === 'success') {
            this.setIsLoading(true)
            this.bannersService.delete$(banner._id).subscribe({
              next: () => {
                resolve(true)
                this.bannersRepository.notify({
                  code: 'BANNER_DELETED',
                })
              },
              error: (error: HttpErrorResponse) => {
                resolve(false)
                this.setError(error)
              },
            })
          }
        })
    })
  }

  // Reducers

  setData(data: Page<Banner>) {
    this.state.set({
      ...this.state(),
      data: data.data,
      totalCount: data.totalCount,
      isInitialized: true,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setFilters(filters?: BannerSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...filters },
    })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  addFilter(filter: BannerSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...this.state().filters, ...filter },
    })
  }

  removeFilter(filter: BannerSearchParams) {
    const currentFilters = this.state().filters
    if (currentFilters) {
      // Create a new filters object without the specified filter properties
      const updatedFilters = { ...currentFilters }

      // Remove each property specified in the filter parameter
      for (const key of Object.keys(filter)) {
        if (key in updatedFilters) {
          delete updatedFilters[key as keyof BannerSearchParams]
        }
      }

      this.state.set({
        ...this.state(),
        isInitialized: true,
        filters: updatedFilters,
      })
    }
  }

  resetFilters() {
    this.state.set({
      ...this.state(),
      filters: {},
    })
  }
}
